import { ConnectorArt } from '@/components/points/ConnectorArt';
import { mapConnectorState } from '@/lib/connectorState';
import { connectorLabel } from '@/lib/connectorLabel';
import type { ConnectorOut } from '@/api/points';

/** Tons da pílula de status (cores do design V5). */
const STATUS_TONE: Record<string, { bg: string; fg: string; dot: string }> = {
  green: { bg: 'rgba(0,192,114,0.12)', fg: '#008A4F', dot: '#00C072' },
  blue: { bg: 'rgba(0,144,226,0.12)', fg: '#0090E2', dot: '#0090E2' },
  amber: { bg: 'rgba(255,185,42,0.18)', fg: '#B47308', dot: '#FFB92A' },
  red: { bg: 'rgba(238,38,62,0.10)', fg: '#D5152B', dot: '#EE263E' },
  muted: { bg: 'rgba(0,27,116,0.05)', fg: 'rgba(0,27,116,0.55)', dot: 'rgba(0,27,116,0.4)' }
};

/** Tom + label de status do conector. Reusa `mapConnectorState`; em CHARGING
 *  mostra "Carregando · {soc}%" como na referência. */
function connectorStatusInfo(state: string | null, soc: number | null) {
  const info = mapConnectorState(state);
  const tone = STATUS_TONE[info.tone] ?? STATUS_TONE.muted;
  let label = info.label;
  if ((state ?? '').toUpperCase() === 'CHARGING') {
    label = soc != null ? `Carregando · ${Math.round(soc)}%` : 'Carregando';
  }
  return { tone, label };
}

/** Pílula de status do conector (card grande V5). */
function ConnectorStatusPill({
  state,
  soc
}: {
  state: string | null;
  soc: number | null;
}) {
  const { tone, label } = connectorStatusInfo(state, soc);
  return (
    <span
      className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold [@media(max-height:740px)]:mt-1.5 [@media(max-height:740px)]:py-0.5"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.dot }} />
      {label}
    </span>
  );
}

function CheckGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

/** Card de seleção de conector (design V5 · A · contorno): ilustração CCS2 +
 *  "Conector N" + pílula de status. Compartilhado entre a landing pública
 *  `/qr/:stationId` (variante `compact`, design V6 uma página) e o
 *  `ConfigureCharge` (tamanho cheio), pra visual idêntico.
 *  `disabled=true` (cabo em uso/indisponível) → não-clicável, sem contorno. */
export function ConnectorSelectCard({
  connector,
  selected,
  onClick,
  disabled = false,
  compact = false,
  letterLabel = false
}: {
  connector: ConnectorOut;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  /** Variante compacta (V6): padding 12, ícone 46, nome 16px — cabe na
   *  one-page sem scroll. Default mantém o card cheio do ConfigureCharge. */
  compact?: boolean;
  /** Rótulo por letra ("Conector A/B") em vez de número — Riseon DC rotula
   *  as tomadas físicas como A e B, e o app mostrando "1 e 2" confundia
   *  (feedback cliente 2026-07-02 nº 9). Vem da capability
   *  `connector_letters` do ponto (dado, não código — §3.6). */
  letterLabel?: boolean;
}) {
  const label = `Conector ${connectorLabel(connector.connector_number, letterLabel)}`;
  const showSelected = selected && !disabled;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={showSelected}
      className={`relative flex flex-col items-center rounded-[18px] border-2 bg-white text-center transition disabled:cursor-not-allowed disabled:opacity-60 ${
        compact ? 'p-3' : 'p-3.5'
      }`}
      style={{
        borderColor: showSelected ? '#001B74' : 'transparent',
        boxShadow: showSelected
          ? '0 4px 14px rgba(0,27,116,0.10)'
          : '0 1px 2px rgba(0,27,116,0.04)'
      }}
    >
      <span
        className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full"
        style={{
          background: showSelected ? '#001B74' : 'transparent',
          border: showSelected ? 'none' : '1.5px solid rgba(0,27,116,0.2)',
          color: '#FFFFFF'
        }}
      >
        {showSelected && <CheckGlyph />}
      </span>

      <div
        className={`flex w-full items-center justify-center ${
          compact ? 'mb-1.5 mt-0.5 min-h-[44px]' : 'mb-2.5 mt-1.5 min-h-[86px]'
        }`}
      >
        {compact ? (
          <>
            {/* Telas baixas: ícone menor pra one-page continuar sem scroll */}
            <span className="[@media(max-height:740px)]:hidden">
              <ConnectorArt type={connector.type} size={52} />
            </span>
            <span className="hidden [@media(max-height:740px)]:inline">
              <ConnectorArt type={connector.type} size={40} />
            </span>
          </>
        ) : (
          <ConnectorArt type={connector.type} size={76} />
        )}
      </div>

      <span
        className="text-bow-royal"
        style={{
          fontFamily: 'Chillax, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: compact ? 16 : 18,
          letterSpacing: '-0.015em'
        }}
      >
        {label}
      </span>

      <ConnectorStatusPill state={connector.state} soc={connector.soc_percent} />
    </button>
  );
}

